import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';

const RecordEditor = ({ table, schema, record, isNew, onSave, onClose }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (isNew) {
      // Initialize with default values or empty strings
      const initialData = {};
      schema.columns.forEach(col => {
        if (!col.isPrimaryKey) {
          initialData[col.name] = '';
        }
      });
      setFormData(initialData);
    } else {
      setFormData({ ...record });
    }
  }, [record, isNew, schema]);

  const handleChange = (columnName, value) => {
    setFormData(prev => ({
      ...prev,
      [columnName]: value
    }));
    // Clear error for this field
    if (errors[columnName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[columnName];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    schema.columns.forEach(col => {
      // Skip primary key validation for new records (auto-generated)
      if (isNew && col.isPrimaryKey) return;

      const value = formData[col.name];

      // Check required fields
      if (!col.nullable && !col.default && (value === '' || value === null || value === undefined)) {
        newErrors[col.name] = 'This field is required';
      }

      // Check max length for strings
      if (col.maxLength && value && value.length > col.maxLength) {
        newErrors[col.name] = `Maximum length is ${col.maxLength} characters`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);

      // Convert empty strings to null for nullable fields
      const cleanedData = {};
      Object.keys(formData).forEach(key => {
        const col = schema.columns.find(c => c.name === key);
        if (col && !col.isPrimaryKey) {
          if (formData[key] === '' && col.nullable) {
            cleanedData[key] = null;
          } else if (formData[key] !== '') {
            // Type conversion
            if (col.type === 'integer' || col.type === 'bigint') {
              cleanedData[key] = parseInt(formData[key], 10);
            } else if (col.type === 'numeric' || col.type === 'double precision') {
              cleanedData[key] = parseFloat(formData[key]);
            } else if (col.type === 'boolean') {
              cleanedData[key] = formData[key] === 'true' || formData[key] === true;
            } else if (col.type === 'jsonb' || col.type === 'json') {
              try {
                cleanedData[key] = JSON.parse(formData[key]);
              } catch (err) {
                cleanedData[key] = formData[key];
              }
            } else if (col.type === 'ARRAY') {
              // Handle array types
              if (typeof formData[key] === 'string') {
                try {
                  cleanedData[key] = JSON.parse(formData[key]);
                } catch (err) {
                  cleanedData[key] = formData[key].split(',').map(v => v.trim());
                }
              } else {
                cleanedData[key] = formData[key];
              }
            } else {
              cleanedData[key] = formData[key];
            }
          }
        }
      });

      await onSave(cleanedData, isNew);
    } catch (err) {
      console.error('Error saving record:', err);
      setSaveError(err.message || 'Failed to save record');
    } finally {
      setSaving(false);
    }
  };

  const getInputType = (column) => {
    if (column.type === 'boolean') return 'select';
    if (column.type === 'integer' || column.type === 'bigint' || column.type === 'numeric') return 'number';
    if (column.type === 'timestamp with time zone' || column.type === 'timestamp without time zone') return 'datetime-local';
    if (column.type === 'date') return 'date';
    if (column.type === 'time') return 'time';
    if (column.type === 'jsonb' || column.type === 'json' || column.type === 'ARRAY') return 'textarea';
    if (column.type === 'text') return 'textarea';
    return 'text';
  };

  const renderInput = (column) => {
    const inputType = getInputType(column);
    const value = formData[column.name] || '';
    const hasError = errors[column.name];

    const baseClasses = `mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
      hasError
        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
    }`;

    if (inputType === 'select') {
      return (
        <select
          value={value}
          onChange={(e) => handleChange(column.name, e.target.value)}
          className={baseClasses}
          disabled={column.isPrimaryKey && !isNew}
        >
          <option value="">Select...</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }

    if (inputType === 'textarea') {
      return (
        <textarea
          value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
          onChange={(e) => handleChange(column.name, e.target.value)}
          rows={4}
          className={baseClasses}
          disabled={column.isPrimaryKey && !isNew}
          placeholder={column.type === 'jsonb' || column.type === 'json' ? '{"key": "value"}' : ''}
        />
      );
    }

    return (
      <input
        type={inputType}
        value={value}
        onChange={(e) => handleChange(column.name, e.target.value)}
        className={baseClasses}
        disabled={column.isPrimaryKey && !isNew}
        step={column.type === 'numeric' ? 'any' : undefined}
      />
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {isNew ? `Create New ${table.displayName} Record` : `Edit ${table.displayName} Record`}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-4">
            {saveError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-red-800">Error Saving Record</h4>
                    <p className="mt-1 text-sm text-red-700">{saveError}</p>
                  </div>
                </div>
              </div>
            )}

            {schema.columns
              .filter(col => !col.isPrimaryKey || !isNew)
              .map((column) => (
                <div key={column.name}>
                  <label className="block text-sm font-medium text-gray-700">
                    {column.name}
                    {column.isPrimaryKey && (
                      <span className="ml-2 text-blue-600" title="Primary Key">🔑</span>
                    )}
                    {!column.nullable && !column.default && (
                      <span className="ml-1 text-red-600">*</span>
                    )}
                  </label>
                  <div className="mt-1">
                    {renderInput(column)}
                    <div className="mt-1 flex items-center justify-between">
                      <p className="text-xs text-gray-500">
                        Type: {column.type}
                        {column.maxLength && ` (max ${column.maxLength})`}
                        {column.nullable && ' • nullable'}
                        {column.default && ` • default: ${column.default}`}
                      </p>
                      {errors[column.name] && (
                        <p className="text-xs text-red-600">{errors[column.name]}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isNew ? 'Create' : 'Save Changes'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecordEditor;
